// Custom hook for accessing budget data (mock implementation)
import { useState, useEffect } from 'react';
import type {
  ProjectBudgetData,
  BudgetSummary,
  CostBudget,
  SalesBudget,
  ResourceBudget,
  AllBudgetsView,
  BudgetAlert,
  BudgetRevision,
} from '@/types/budget.types';
import { getBudgetDataByProjectId } from '@/lib/mock-data/budget-mock-data';

// Simulate API delay
const SIMULATED_DELAY = 300;

function simulateApiCall<T>(data: T | undefined): Promise<T> {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (data) {
        resolve(data);
      } else {
        reject(new Error('Data not found'));
      }
    }, SIMULATED_DELAY);
  });
}

// ============================================================================
// MAIN BUDGET DATA HOOK
// ============================================================================

export function useBudgetData(projectId: string) {
  const [data, setData] = useState<ProjectBudgetData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    console.log('[useBudgetData] Fetching budget for project:', projectId);
    const budgetData = getBudgetDataByProjectId(projectId);
    console.log('[useBudgetData] Budget data found:', budgetData ? 'Yes' : 'No');

    simulateApiCall(budgetData)
      .then((budgetData) => {
        console.log('[useBudgetData] Budget data loaded successfully');
        setData(budgetData);
        setLoading(false);
      })
      .catch((err) => {
        console.error('[useBudgetData] Error loading budget:', err);
        setError(err.message);
        setLoading(false);
      });
  }, [projectId]);

  return { data, loading, error };
}

// ============================================================================
// SPECIFIC SECTION HOOKS
// ============================================================================

export function useBudgetSummary(projectId: string) {
  const [data, setData] = useState<BudgetSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    simulateApiCall(getBudgetDataByProjectId(projectId))
      .then((budgetData) => {
        setData(budgetData.summary);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [projectId]);

  return { data, loading, error };
}

export function useCostBudget(projectId: string) {
  const [data, setData] = useState<CostBudget | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    simulateApiCall(getBudgetDataByProjectId(projectId))
      .then((budgetData) => {
        setData(budgetData.costBudget);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [projectId]);

  return { data, loading, error };
}

export function useSalesBudget(projectId: string) {
  const [data, setData] = useState<SalesBudget | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    simulateApiCall(getBudgetDataByProjectId(projectId))
      .then((budgetData) => {
        setData(budgetData.salesBudget);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [projectId]);

  return { data, loading, error };
}

export function useResourceBudget(projectId: string) {
  const [data, setData] = useState<ResourceBudget | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    simulateApiCall(getBudgetDataByProjectId(projectId))
      .then((budgetData) => {
        setData(budgetData.resourceBudget);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [projectId]);

  return { data, loading, error };
}

export function useAllBudgetsView(projectId: string) {
  const [data, setData] = useState<AllBudgetsView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    simulateApiCall(getBudgetDataByProjectId(projectId))
      .then((budgetData) => {
        setData(budgetData.allBudgets);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [projectId]);

  return { data, loading, error };
}

export function useBudgetAlerts(projectId: string) {
  const [data, setData] = useState<BudgetAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    simulateApiCall(getBudgetDataByProjectId(projectId))
      .then((budgetData) => {
        setData(budgetData.alerts);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [projectId]);

  const dismissAlert = (alertId: string) => {
    setData((prevData) =>
      prevData.map((alert) =>
        alert.id === alertId ? { ...alert, dismissed: true } : alert
      )
    );
  };

  return { data, loading, error, dismissAlert };
}

export function useBudgetRevisions(projectId: string) {
  const [data, setData] = useState<BudgetRevision[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    simulateApiCall(getBudgetDataByProjectId(projectId))
      .then((budgetData) => {
        setData(budgetData.revisions);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [projectId]);

  return { data, loading, error };
}

// ============================================================================
// MUTATION HOOKS (for future editing functionality)
// ============================================================================

export function useUpdateBudget() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateBudget = async (projectId: string, budgetData: Partial<ProjectBudgetData>) => {
    setLoading(true);
    setError(null);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, SIMULATED_DELAY));
      
      // In a real implementation, this would call the API
      console.log('Budget updated:', { projectId, budgetData });
      
      setLoading(false);
      return { success: true };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
      setLoading(false);
      return { success: false };
    }
  };

  return { updateBudget, loading, error };
}

export function useCreateBudgetRevision() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createRevision = async (
    projectId: string,
    revisionData: Partial<BudgetRevision>
  ) => {
    setLoading(true);
    setError(null);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, SIMULATED_DELAY));
      
      // In a real implementation, this would call the API
      console.log('Revision created:', { projectId, revisionData });
      
      setLoading(false);
      return { success: true };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create revision failed');
      setLoading(false);
      return { success: false };
    }
  };

  return { createRevision, loading, error };
}
